import { useState } from "react";
import { getAllOrders } from "../../services/orderApi";
import { getOrderStatusHistory } from "../../services/orderStatusApi";

const OrdersPage = () => {
  // ==============================
  // 1️⃣ SINGLE DATE SEARCH
  // ==============================
  const [singleDate, setSingleDate] = useState("");
  const [singleDateOrders, setSingleDateOrders] = useState([]);

  // ==============================
  // 2️⃣ DATE RANGE + STATUS SEARCH
  // ==============================
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");
  const [filteredOrders, setFilteredOrders] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ==============================
  // ENRICH STATUS
  // ==============================
  const enrichOrders = async (orders) => {
    return Promise.all(
      orders.map(async (order) => {
        try {
          const history = await getOrderStatusHistory(order.id);
          const latest = history?.length
            ? history[history.length - 1]
            : null;

          return {
            ...order,
            latestStatus: latest?.status || "submitted",
            lastUpdated: latest?.created_at || null,
          };
        } catch {
          return {
            ...order,
            latestStatus: "submitted",
            lastUpdated: null,
          };
        }
      })
    );
  };

  // ==============================
  // 🔵 SINGLE DATE SEARCH
  // ==============================
  const handleSingleDateSearch = async () => {
    if (!singleDate) return alert("Select date");

    try {
      setLoading(true);
      setError("");

      const data = await getAllOrders();

      const filtered = data.filter((o) => {
        const d = new Date(o.created_at).toISOString().split("T")[0];
        return d === singleDate;
      });

      const enriched = await enrichOrders(filtered);

      setSingleDateOrders(enriched);
    } catch {
      setError("Failed single date search");
    } finally {
      setLoading(false);
    }
  };

  // ==============================
  // 🟢 RANGE + STATUS SEARCH
  // ==============================
  const handleRangeSearch = async () => {
    if (!fromDate || !toDate) {
      alert("Select date range");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const data = await getAllOrders();

      const filtered = data.filter((o) => {
        const d = new Date(o.created_at).toISOString().split("T")[0];
        return d >= fromDate && d <= toDate;
      });

      const enriched = await enrichOrders(filtered);

      const final =
        status === ""
          ? enriched
          : enriched.filter((o) => o.latestStatus === status);

      setFilteredOrders(final);
    } catch {
      setError("Failed range search");
    } finally {
      setLoading(false);
    }
  };

  // ==============================
  // STATUS COLOR
  // ==============================
  const getStatusColor = (s) => {
    switch (s) {
      case "processing":
        return "#f59e0b";
      case "shipped":
        return "#3b82f6";
      case "delivered":
        return "#22c55e";
      default:
        return "#6b7280";
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>📦 Admin Orders Panel</h2>

      {error && <p style={{ color: "red" }}>{error}</p>}
      {loading && <p>Loading...</p>}

      {/* ==============================
          🔵 SINGLE DATE SEARCH
      ============================== */}
      <div style={{ marginBottom: "40px" }}>
        <h3>📅 Search by Single Date</h3>

        <input
          type="date"
          value={singleDate}
          onChange={(e) => setSingleDate(e.target.value)}
        />

        <button onClick={handleSingleDateSearch} style={{ marginLeft: "10px" }}>
          Search
        </button>

        {singleDateOrders.length > 0 && (
          <table border="1" width="100%" cellPadding="10" style={{ marginTop: "10px" }}>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Last Update</th>
                <th>Time</th>
              </tr>
            </thead>

            <tbody>
              {singleDateOrders.map((o) => (
                <tr key={o.id}>
                  <td>{o.order_id}</td>
                  <td>{o.user_name}</td>
                  <td>
                    <span style={{
                      background: getStatusColor(o.latestStatus),
                      color: "#fff",
                      padding: "4px 8px",
                      borderRadius: "6px",
                    }}>
                      {o.latestStatus}
                    </span>
                  </td>
                  <td>
                    {o.lastUpdated
                      ? new Date(o.lastUpdated).toLocaleString()
                      : "N/A"}
                  </td>
                  <td>
                    {new Date(o.created_at).toLocaleTimeString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ==============================
          🟢 RANGE + STATUS SEARCH
      ============================== */}
      <div>
        <h3>📊 Search by Date Range + Status</h3>

        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />

        <span style={{ margin: "0 10px" }}>to</span>

        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{ marginLeft: "10px" }}
        >
          <option value="">All Status</option>
          <option value="submitted">Submitted</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
        </select>

        <button onClick={handleRangeSearch} style={{ marginLeft: "10px" }}>
          Search
        </button>

        {filteredOrders.length > 0 && (
          <table border="1" width="100%" cellPadding="10" style={{ marginTop: "10px" }}>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Last Update</th>
                <th>Time</th>
              </tr>
            </thead>

            <tbody>
              {filteredOrders.map((o) => (
                <tr key={o.id}>
                  <td>{o.order_id}</td>
                  <td>{o.user_name}</td>

                  <td>
                    <span style={{
                      background: getStatusColor(o.latestStatus),
                      color: "#fff",
                      padding: "4px 8px",
                      borderRadius: "6px",
                    }}>
                      {o.latestStatus}
                    </span>
                  </td>

                  <td>
                    {o.lastUpdated
                      ? new Date(o.lastUpdated).toLocaleString()
                      : "N/A"}
                  </td>

                  <td>
                    {new Date(o.created_at).toLocaleTimeString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;